import React from 'react';
import { useNavigation } from '@react-navigation/native';
import type { NativeStackNavigationProp } from '@react-navigation/native-stack';
import { useShakeMonitor } from '@sweetgreen/garnish';

import { useIsLoggedIn } from '@order/AuthMachine';
import { useTelemetry } from '@order/Telemetry';

import type { BillingAccount } from '../../graphql/types';
import type {
  AppStackParamList,
  ModalStackParamList,
} from '../../navigation/AppNavigation.props';
import { useScanToPayFeatureFlag } from './ScanToPay.featureFlag';

export const useScanToPayNavigation = () => {
  const {
    handleOpenScanToPay,
    handleSelectPaymentMethod,
    handleSelectAddPaymentMethod,
  } = useAppStackNavigation();
  const navigation =
    useNavigation<NativeStackNavigationProp<ModalStackParamList>>();

  const goToOptions = React.useCallback(() => {
    navigation.navigate('ScanToPay');
  }, [navigation]);

  const goToPayments = React.useCallback(() => {
    navigation.navigate('Payments');
  }, [navigation]);

  const goToGiftCode = React.useCallback(() => {
    navigation.navigate('CreditAdd');
  }, [navigation]);

  const goBack = React.useCallback(() => {
    if (navigation.canGoBack()) navigation.goBack();
    else goToOptions();
  }, [navigation, goToOptions]);

  return {
    handleOpenScanToPay,
    handleSelectPaymentMethod,
    handleSelectAddPaymentMethod,
    goToOptions,
    goToPayments,
    goToGiftCode,
    goBack,
  };
};

// Shake to summon scan to pay, disabled if logged out or the feature flag is off.
export const useShakeToSummonScanToPay = () => {
  const isLoggedIn = useIsLoggedIn();
  const isScanToPayEnabled = useScanToPayFeatureFlag();
  const { track } = useTelemetry();

  const navigation =
    useNavigation<NativeStackNavigationProp<AppStackParamList>>();

  const onShake = React.useCallback(() => {
    if (!isScanToPayEnabled || !isLoggedIn) return;
    navigation.navigate('Modal', { screen: 'ScanToPay' });
    track('scan-to-pay.shake');
  }, [isScanToPayEnabled, isLoggedIn, navigation, track]);

  useShakeMonitor(onShake);
};

const useAppStackNavigation = () => {
  const navigation =
    useNavigation<NativeStackNavigationProp<AppStackParamList>>();
  const handleOpenScanToPay = React.useCallback(() => {
    navigation.navigate('Modal', { screen: 'ScanToPay' });
  }, [navigation]);
  const handleSelectPaymentMethod = React.useCallback(
    ({ id }: BillingAccount) => {
      navigation.navigate('Modal', {
        screen: 'PaymentMethodEdit',
        params: { paymentMethodId: id },
      });
    },
    [navigation],
  );
  const handleSelectAddPaymentMethod = React.useCallback(() => {
    navigation.navigate('Modal', {
      screen: 'PaymentMethodAdd',
    });
  }, [navigation]);

  return {
    handleOpenScanToPay,
    handleSelectPaymentMethod,
    handleSelectAddPaymentMethod,
  };
};
